// Number formatting follows the market's own language, not its currency.
//
// Deriving the locale from the currency code cannot tell two EUR markets apart: Germany writes
// 1.234,56 and Finland writes 1 234,56, but both pay in EUR. The old currency -> locale switch
// therefore formatted every EUR market as German, which was invisible while Germany was the only
// one.
//
// The page already states which market it is: _Root.cshtml renders commerce-lang on <html>, and
// basket.js, checkout.js and the Vuex store modules all read it for exactly this purpose.
//
// Matching is exact, never truncated. "fr-CH" must not be shortened to "fr" — that is the
// truncation trap documented in docs/adding-a-language.md — so an unlisted culture falls through
// to the currency map below rather than being coerced into a neighbour's format.

// Commerce markets only, keyed by the branch culture as commerce-lang reports it.
const MARKET_LOCALES = {
    da: 'da',
    sv: 'sv',
    no: 'no',
    de: 'de',
    fi: 'fi'
}

// The pre-existing fallback. Every entry here agrees with MARKET_LOCALES for the market that uses
// it, so pages without a commerce-lang attribute keep formatting exactly as they did before.
const CURRENCY_LOCALES = {
    DKK: 'da',
    EUR: 'de',
    SEK: 'sv',
    NOK: 'no'
}

const FALLBACK_LOCALE = 'da'

// Exported for testing: this is the whole decision, and it is worth being able to reach directly.
export function resolveLocale(currency) {
    let marketLang = null

    try {
        marketLang = document.documentElement.getAttribute('commerce-lang')
    } catch (e) {
        // No document (server-side render, unit test): fall through to the currency map.
    }

    if (marketLang && MARKET_LOCALES[marketLang.toLowerCase()]) {
        return MARKET_LOCALES[marketLang.toLowerCase()]
    }

    return CURRENCY_LOCALES[currency] || FALLBACK_LOCALE
}

function currencyFormatter(currency, number) {
    const currencyLabel = currency === '' || currency === null || currency === undefined ? 'DKK' : currency
    const locale = resolveLocale(currencyLabel)

    return `${currencyLabel} ${new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(number)}`
}

export default currencyFormatter
